import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Zap, HardDrive, BarChart3, Palette, Users, Check, Loader2, Crown, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  'hard-drive': HardDrive,
  'zap': Zap,
  'bar-chart': BarChart3,
  'palette': Palette,
  'users': Users,
};

const GRADIENT_MAP = {
  extra_storage: 'from-blue-500 to-cyan-500',
  priority_ai: 'from-violet-500 to-purple-500',
  advanced_analytics: 'from-emerald-500 to-teal-500',
  custom_branding: 'from-amber-500 to-orange-500',
  collaboration_pro: 'from-rose-500 to-pink-500',
};

const AddonsPage = () => {
  const { user } = useAuth();
  const [addons, setAddons] = useState([]);
  const [myAddons, setMyAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API}/premium/addons`),
        axios.get(`${API}/premium/my-addons`),
      ]);
      setAddons(allRes.data.addons || []);
      setMyAddons(myRes.data.addons || []);
    } catch (e) {
      toast.error('Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  };

  const activeIds = new Set(myAddons.map(a => a.addon_id));

  const activate = async (addonId) => {
    setActivating(addonId);
    try {
      const res = await axios.post(`${API}/premium/addons/activate`, { addon_id: addonId });
      toast.success(res.data.message);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
    } finally {
      setActivating(null);
    }
  };

  const deactivate = async (addonId) => {
    setActivating(addonId);
    try {
      await axios.post(`${API}/premium/addons/deactivate`, { addon_id: addonId });
      toast.success('Add-on deactivated');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate');
    } finally {
      setActivating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="addons-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] tracking-tight">
          Premium Add-ons
        </h1>
        <p className="text-[var(--isp-body)] mt-2 text-base">
          Supercharge your study experience with individual feature upgrades
        </p>
      </div>

      {/* Active Add-ons Summary */}
      {myAddons.length > 0 && (
        <div className=" border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
          <h2 className="text-sm font-semibold text-emerald-300 mb-3 flex items-center gap-2">
            <Crown className="w-4 h-4" />
            Your Active Add-ons ({myAddons.length})
          </h2>
          <div className="flex gap-3 flex-wrap">
            {myAddons.map(a => (
              <div key={a.addon_id} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 text-sm text-emerald-300">
                <Check className="w-3.5 h-3.5" />
                {a.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add-on Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {addons.map((addon, i) => {
          const IconComp = ICON_MAP[addon.icon] || Zap;
          const isActive = activeIds.has(addon.id);
          const gradient = GRADIENT_MAP[addon.id] || 'from-violet-500 to-indigo-500';

          return (
            <motion.div
              key={addon.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className={` border overflow-hidden transition-all ${
                isActive
                  ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                  : 'border-[var(--isp-glass-border)] bg-[var(--isp-glass)]'
              }`}
              data-testid={`addon-card-${addon.id}`}
            >
              {/* Header with gradient */}
              <div className={`p-4 bg-gradient-to-r ${gradient} bg-opacity-10`}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <IconComp className="w-5 h-5 text-white" />
                  </div>
                  {isActive && (
                    <span className="text-[10px] px-2.5 py-1 rounded-full bg-white/20 text-white font-semibold">
                      ACTIVE
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-white mt-3">{addon.name}</h3>
                <p className="text-sm text-white/80 mt-1">{addon.description}</p>
              </div>

              {/* Features */}
              <div className="p-4 space-y-4">
                <ul className="space-y-2">
                  {addon.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-[var(--isp-body)]">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Price + Action */}
                <div className="flex items-center justify-between pt-2 border-t border-[var(--isp-glass-border)]">
                  <div>
                    <span className="text-2xl font-bold text-[var(--isp-heading)]">${addon.price_usd}</span>
                    <span className="text-xs text-[var(--isp-muted)]">/{addon.billing}</span>
                  </div>
                  {isActive ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deactivate(addon.id)}
                      disabled={activating === addon.id}
                      className="rounded-xl gap-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
                      data-testid={`deactivate-${addon.id}`}
                    >
                      {activating === addon.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                      Remove
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => activate(addon.id)}
                      disabled={activating === addon.id}
                      className={`rounded-xl gap-2 bg-gradient-to-r ${gradient} text-white`}
                      data-testid={`activate-${addon.id}`}
                    >
                      {activating === addon.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      Activate
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default AddonsPage;
